import * as React from "react";
import { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { listAppUsers, userDisplay, labelForUserKey, type AppUser } from "../../services/userService";
import { resolvePeople } from "../vorgang/shared/people";
import { alleDokumente, alleFenster } from "../windowManager/browserFenster/fensterRegister";

const useStyles = makeStyles({
    field: {
        ...shorthands.border("1px", "solid", "#e3e0e7"),
        borderRadius: "6px",
        ...shorthands.padding("4px", "6px"),
        backgroundColor: "#ffffff",
        display: "flex",
        flexWrap: "wrap",
        gap: "4px",
        minHeight: "32px",
        cursor: "text",
        ":focus-within": { ...shorthands.borderColor("#4d2051") },
    },
    chip: {
        // Warme Plum-Palette statt des grünen Chips „außerhalb der Palette"
        // (SP14, Review-Screenshot 15.33.51). Passt zu .vgd-minibadge / aub-50.
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        ...shorthands.padding("2px", "8px"),
        borderRadius: "999px",
        backgroundColor: "#f8eef5",
        color: "#7d2169",
        fontSize: "12px",
        fontWeight: 600,
    },
    chipRemove: {
        appearance: "none",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        color: "#7d2169",
        fontSize: "12px",
        ...shorthands.padding("0"),
    },
    input: {
        flex: 1,
        minWidth: "120px",
        ...shorthands.border("none"),
        ...shorthands.padding("4px"),
        backgroundColor: "transparent",
        fontFamily: "inherit",
        fontSize: "13px",
        outlineStyle: "none",
    },
    dropdown: {
        position: "absolute",
        zIndex: 30,
        backgroundColor: "#ffffff",
        ...shorthands.border("1px", "solid", "#e3e0e7"),
        borderRadius: "6px",
        boxShadow: "0 8px 24px rgba(20,8,20,0.12)",
        maxHeight: "240px",
        overflowY: "auto",
        marginTop: "4px",
        minWidth: "240px",
    },
    option: {
        ...shorthands.padding("6px", "12px"),
        cursor: "pointer",
        fontSize: "13px",
        ":hover": { backgroundColor: "#faf6fb" },
    },
    optionEmpty: {
        ...shorthands.padding("8px", "12px"),
        color: "#6f6c75",
        fontSize: "12px",
        fontStyle: "italic",
    },
    wrap: {
        position: "relative",
    },
});

type Props = {
    value: string[];
    onChange: (next: string[]) => void;
    placeholder?: string;
    /** Stored identifier — typically the user's id. */
    storeAs?: "id" | "email" | "display";
    /**
     * Chip-Darstellung: "name" (Default, voller Anzeigename) oder "kuerzel"
     * (KMVS-Kürzel wie MEJ — Muster vorgang/shared/people.ts, Mail→Vorgang SP3;
     * der volle Name bleibt als title-Tooltip erhalten).
     */
    chip?: "name" | "kuerzel";
};

export const UserMultiPicker: React.FC<Props> = ({
    value,
    onChange,
    placeholder = "Person tippen oder wählen…",
    storeAs = "id",
    chip = "name",
}) => {
    const styles = useStyles();
    const [users, setUsers] = useState<AppUser[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);

    useEffect(() => {
        listAppUsers()
            .then(setUsers)
            .finally(() => setLoaded(true));
    }, []);

    // Position the portaled dropdown under the field. `position: fixed` in a
    // body-portal escapes any scrollable/overflow:auto ancestor (e.g. the
    // Vorgang-Fenster body), so the list is never clipped.
    useEffect(() => {
        if (!open) return;
        const reposition = () => {
            const r = wrapRef.current?.getBoundingClientRect();
            if (r) setPos({ top: r.bottom + 4, left: r.left, width: r.width });
        };
        reposition();
        alleFenster.addEventListener("scroll", reposition, true);
        alleFenster.addEventListener("resize", reposition);
        return () => {
            alleFenster.removeEventListener("scroll", reposition, true);
            alleFenster.removeEventListener("resize", reposition);
        };
    }, [open]);

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            const t = e.target as Node;
            if (wrapRef.current?.contains(t) || dropdownRef.current?.contains(t)) return;
            setOpen(false);
        };
        alleDokumente.addEventListener("pointerdown", onDocClick);
        return () => alleDokumente.removeEventListener("pointerdown", onDocClick);
    }, []);

    const storeKey = (u: AppUser): string => {
        if (storeAs === "email" && u.email) return u.email;
        if (storeAs === "display") return userDisplay(u);
        return u.id;
    };

    const findStored = (key: string): AppUser | undefined => {
        const lc = key.trim().toLowerCase();
        if (!lc) return undefined;
        return users.find(
            (u) =>
                u.id.toLowerCase() === lc ||
                (u.email ?? "").toLowerCase() === lc ||
                userDisplay(u).toLowerCase() === lc,
        );
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        const taken = new Set(value);
        return users
            .filter((u) => !taken.has(storeKey(u)))
            .filter((u) => {
                if (!q) return true;
                return (
                    userDisplay(u).toLowerCase().includes(q) ||
                    (u.email ?? "").toLowerCase().includes(q) ||
                    (u.username ?? "").toLowerCase().includes(q)
                );
            })
            .slice(0, 20);
    }, [users, query, value, storeAs]);

    const addUser = (u: AppUser) => {
        const key = storeKey(u);
        if (!value.includes(key)) onChange([...value, key]);
        setQuery("");
        setOpen(false);
    };

    const removeKey = (key: string) => {
        onChange(value.filter((v) => v !== key));
    };

    return (
        <div ref={wrapRef} className={styles.wrap}>
            <div
                className={styles.field}
                onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.tagName !== "BUTTON") setOpen(true);
                }}
            >
                {value.map((v) => {
                    const label = labelForUserKey(v, users, { loading: !loaded });
                    const person = chip === "kuerzel" ? resolvePeople([v], users)[0] : undefined;
                    return (
                        <span key={v} className={styles.chip} title={person ? person.name : v}>
                            {person ? person.initials : label}
                            <button
                                type="button"
                                className={styles.chipRemove}
                                onClick={() => removeKey(v)}
                                aria-label="Entfernen"
                            >
                                ×
                            </button>
                        </span>
                    );
                })}
                <input
                    className={styles.input}
                    placeholder={value.length === 0 ? placeholder : ""}
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={(e) => {
                        if (e.key === "Backspace" && !query && value.length > 0) {
                            removeKey(value[value.length - 1]);
                        }
                        if (e.key === "Enter" && filtered.length > 0) {
                            e.preventDefault();
                            addUser(filtered[0]);
                        }
                    }}
                />
            </div>
            {open && pos &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={styles.dropdown}
                        style={{
                            position: "fixed",
                            top: pos.top,
                            left: pos.left,
                            width: pos.width,
                            marginTop: 0,
                            zIndex: 100000,
                        }}
                    >
                        {filtered.length === 0 ? (
                            <div className={styles.optionEmpty}>Keine Treffer.</div>
                        ) : (
                            filtered.map((u) => (
                                <div
                                    key={u.id}
                                    className={styles.option}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        addUser(u);
                                    }}
                                >
                                    {userDisplay(u)}
                                    {u.email && (
                                        <span style={{ color: "#6f6c75", marginLeft: 8, fontSize: 11 }}>
                                            {u.email}
                                        </span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>,
                    document.body,
                )}
        </div>
    );
};
